import type {IntlShape} from 'react-intl';
import type {PermissionType} from '@/Permissions';

// Pages and row types are named by the pages that host agents, so these components never list them.
export type AgentPage = string;
export type AgentRowType = string;

export const AGENT_RUN_SCOPE = {
	PAGE: 'PAGE',
	ROW: 'ROW',
} as const;

export const AGENT_RUN_STATE = {
	RUNNING: 'RUNNING',
	STOPPED: 'STOPPED',
	NOTHING_FOUND: 'NOTHING_FOUND',
	PRODUCED: 'PRODUCED',
	FAILED: 'FAILED',
} as const;
export type AgentRunState = (typeof AGENT_RUN_STATE)[keyof typeof AGENT_RUN_STATE];

export type AgentText = {
	id: string;
	defaultMessage: string;
};

export type AgentRunScope =
	| {
			kind: typeof AGENT_RUN_SCOPE.PAGE;
			page: AgentPage;
	  }
	| {
			kind: typeof AGENT_RUN_SCOPE.ROW;
			rowId: string;
	  };

export type AgentResult = {
	id: string;
	rowIds: string[];
	content: unknown;
};

export type AgentRunOutcome = {
	state: Exclude<AgentRunState, typeof AGENT_RUN_STATE.RUNNING>;
	results: AgentResult[];
	detail: unknown;
};

export type AgentRunHandle = {
	outcome: Promise<AgentRunOutcome>;
	stop: () => void;
};

export type AgentRunner = {
	start: (scope: AgentRunScope) => AgentRunHandle;
};

export type AgentAcceptOutcome =
	| {
			accepted: true;
	  }
	| {
			accepted: false;
			reason: string;
	  };

export type AgentResultContent = {
	title: string;
	dateLine: string;
	total: string;
};

export type AgentDeclaration = {
	id: string;
	name: AgentText;
	pages: AgentPage[];
	rows: AgentRowType[];
	recordNoun: AgentText;
	permission: PermissionType;
	action: AgentText;
	resultNoun: {
		singular: AgentText;
		plural: AgentText;
	};
	acceptLabel: {
		single: AgentText;
		bulk: AgentText;
	};
	runAgainLabel: AgentText;
	runAgainToast: AgentText;
	doneStateName: AgentText;
	doneAction: {
		label: AgentText;
		perform: (result: AgentResult) => void;
	};
	pillTips: {
		idle: AgentText;
		working: AgentText;
		ready: AgentText;
	};
	describeResult: (result: AgentResult, intl: IntlShape) => AgentResultContent;
	foundText: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	summaryToast: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	notificationText: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	failureReason: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	review: (result: AgentResult) => void;
	accept: (result: AgentResult) => Promise<AgentAcceptOutcome>;
	schedulingTarget: string;
};

export type AgentRegistration = {
	declaration: AgentDeclaration;
	runner: AgentRunner;
};
